/**
 * @module cercaliagl/control/LayerSwitcher
 */
import Control from './Control';
import MapControls from './MapControls';
import MapTypes from '../MapTypes';
import { i18n } from '../dictionary';
import VDropDown, { EventType as DropDownEventType } from '../view/VLayerSwitcher';
import VControlButton from '../view/VControlButton';
import { isMobile, getDOMRelativePosition } from '../view/VUtils';

/**
 * @typedef {Object} LayerSwitcherOptions
 * @property {Array.<string>|undefined} mapLayers  Maps array to be painted. Admitted values:<br/>
 * `cercaliagl.MapTypes.CERCALIA`<br>
 * `cercaliagl.MapTypes.OSM`<br/>
 * `cercaliagl.MapTypes.SATELLITE`<br/>
 * `cercaliagl.MapTypes.DARK`<br/>
 * `cercaliagl.MapTypes.SYLVER`<br/>
 * `cercaliagl.MapTypes.EMPTY`<br/>
 * @property {string} active Active base layer id
 */

/**
 * @classdesc
 * <b>Javascript ES5</b>
 *
 * ```js
 * const control = new cercaliagl.control.LayerSwitcher();
 * ```
 * <br/>
 * This is a layer switcher control. Base layer can be changed via this control.
 * It is possible to add custom layers. You can see some examples in `Examples` page.
 * @constructor
 * @extends {Control}
 * @api
 */
class LayerSwitcher extends Control {
  /**
   * @param {LayerSwitcherOptions} [options] Options
   */
  constructor(options = {}) {
    const controlButton = new VControlButton('layers');
    const element = controlButton.getElement();
    super({
      element,
      className: 'cercaliagl.control.LayerSwitcher',
      name: MapControls.LAYER_SWITCHER,
    });

    /**
     * @private
     * @type {VControlButton}
     */
    this.controlButton_ = controlButton;

    /**
     * @private
     * @type {HTMLElement}
     */
    this.element_ = element;

    /**
     * @private
     * @type {Array.<string>}
     */
    this.mapLayers_ = options.mapLayers !== undefined
      ? options.mapLayers : [MapTypes.CERCALIA, MapTypes.DARK, MapTypes.SILVER, MapTypes.BLANKMAP, MapTypes.OSM, MapTypes.SATELLITE];

    /**
     * @private
     * @type {VDropDown}
     */
    this.dropdown_ = new VDropDown();

    this.initialize_(options.active);
  }

  /**
   * @private
   */
  initialize_(activeBaselayerId) {
    this.controlButton_.setTooltip(i18n('tooltipLayerSwitcher'));
    this.initializeBaselayers_();

    this.dropdown_.setSelected(activeBaselayerId);

    // Events
    this.element_.addEventListener('click', this.handleClick_.bind(this));
    window.addEventListener('resize', this.adjustPosition_.bind(this));
    this.dropdown_.on(DropDownEventType.SELECTED, this.handleBaselayerChange_.bind(this));
  }

  /**
   * @private
   */
  initializeBaselayers_() {
    Object.entries(MapTypes).forEach(([, mapType]) => {
      if (this.mapLayers_.indexOf(mapType) !== -1) {
        this.addLayer(mapType, mapType);
      }
    });
  }

  /**
   * @param {string} id Layer id.
   * @param {string} label Label
   */
  addLayer(id, label) {
    const item = {
      id,
      value: label,
    };
    this.dropdown_.add(item);
  }

  /**
   * @private
   */
  handleClick_() {
    if (!this.dropdown_.isVisible()) {
      const container = this.getMap().getContainer();
      if (!this.dropdown_.getElement().parentElement) {
        this.dropdown_.appendTo(container);
      }

      this.adjustPosition_();

      this.dropdown_.show();
    } else {
      this.dropdown_.hide();
    }
  }

  /**
   * @private
   */
  adjustPosition_() {
    const relativePos = getDOMRelativePosition(this.element_, this.getMap().getContainer());
    const htmlElement = this.dropdown_.getElement();
    htmlElement.style.top = isMobile() ? '' : `${relativePos.top + this.element_.offsetHeight + 4}px`;
    htmlElement.style.right = isMobile() ? `${Math.abs(relativePos.right) + 4}px` : `${Math.abs(relativePos.right)}px`;
    htmlElement.style.bottom = isMobile() ? `${Math.abs(relativePos.bottom) + 42}px` : '';
  }

  /**
   * Open dropdown user interface component
   * @api
   */
  open() {
    this.dropdown_.show();
  }

  /**
   * Close dropdown user interface component
   * @api
   */
  close() {
    this.dropdown_.hide();
  }

  /**
   * @private
   * @param {import("../view/VLayerSwitcher.js").Item} item Item
   */
  handleBaselayerChange_(item) {
    this.dropdown_.hide();
    this.changeBaseLayer(item.id);
  }

  /**
   * Change base layer with a given base layer id.
   * @api
   * @param {string} baselayerId Base layer id.
   */
  changeBaseLayer(baselayerId) {
    const cercaliaMap = this.getCercaliaMap();
    cercaliaMap.changeBaseLayer(baselayerId);
    this.dropdown_.setSelected(baselayerId);
  }
}

export default LayerSwitcher;
